import { Injectable, Injector, NgZone } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { filter } from 'rxjs/operators';
import { StoreEvent, StoreRealtimeService } from '../../../core/services/store-realtime.service';
import { StoreContextService } from '../../../core/services/store-context.service';
import { ToastService } from '../../../core/services/toast.service';
import { CurrencyFormatPipe } from '../../../shared/pipes/currency-format.pipe';
import { LockAwareAlerts } from './lock-aware-alerts';
import { isPosOperatorScreen } from './pos-operator-screen';

/** The cloud's event for a guest pay-at-table payment the floor must act on (TCPAY-1). */
export const TABLECONNECT_PAYMENT_FAILED = 'tableconnect.payment.failed';

/** "Failed": no money was taken, and the guest was told to retry. */
export const TC_PAYMENT_FAILED_TOAST_MS = 6000;

/** "RequiresReview": money TAKEN and not recorded. Up longer: somebody has to act on it. */
export const TC_PAYMENT_REVIEW_TOAST_MS = 12000;

/** How many alerts a locked terminal keeps for its next unlock; the oldest go first. */
export const TC_PAYMENT_HELD_MAX = 5;

/**
 * A held "Failed" alert this old is dropped instead of raised at the unlock.
 * It goes to every terminal, so a Waiter terminal locked for a while collected
 * other waiters' tables, and ten minutes on the guest has paid again or another
 * way. (The same rule as OFP-093's kitchen.send.failed.)
 */
export const TC_PAYMENT_FAILED_HELD_MAX_AGE_MS = 10 * 60 * 1000;

/**
 * A held "RequiresReview" alert is kept until the unlock, but not past a
 * shift: the money stays unrecorded until somebody acts, and the settle
 * screen's TableConnect panel lists it whatever the toast did. The end of the
 * POS session and a change of store drop it sooner (LockAwareAlerts).
 */
export const TC_PAYMENT_REVIEW_HELD_MAX_AGE_MS = 12 * 60 * 60 * 1000;

/** Where pos-shell, the floor, is mounted. It connects the store channel itself. */
const POS_FLOOR_PATH = '/pos/terminal';

/** The two statuses the cloud sends, as `tcPaymentAlertKind` reads them. */
export type TcPaymentAlertKind = 'failed' | 'requiresReview';

/**
 * TCPAY-1 — tell the floor when a guest's pay-at-table payment failed, or was
 * taken and not recorded.
 *
 * The cloud publishes `tableconnect.payment.failed` (TcPaymentEventNotifier)
 * only for a store that opted in (TableConnectPaymentAlertsEnabled), and names
 * no terminal, so every terminal that hears it shows it. `status` is "Failed"
 * (no money taken; the guest was told to retry) or "RequiresReview" (money
 * TAKEN and unrecorded; somebody must open the settle screen, whose
 * TableConnect panel has the details). Nothing on screen is reloaded for it.
 *
 * ROOT-level, started once from AppComponent — not in the POS shell, which is
 * mounted only on `/pos/terminal`. The shell showed it as its own green
 * success toast, so a terminal on the order or settle screen (sibling routes)
 * never saw it. It is now an error toast on every POS operator screen,
 * TC_PAYMENT_FAILED_TOAST_MS for "Failed" and TC_PAYMENT_REVIEW_TOAST_MS for
 * "RequiresReview", held through a lock as OFP-093's print alerts are
 * (LockAwareAlerts, with a queue of its own). One toast per check and per
 * sentence: a repeat while it is on screen is not raised again.
 *
 * `summary` is a JSON string `{tableName, amount, currency}` from a cloud with
 * PR #787. An older cloud sends none, and the toast still shows, with "no
 * table" and no amount.
 */
@Injectable({ providedIn: 'root' })
export class TableConnectPaymentAlertsService {
  private started = false;

  /** The toasts: raised now, or held while the terminal is locked and raised at the unlock. */
  private readonly alerts: LockAwareAlerts;

  /** The store's decimals and grouping, as on the table cards: a KWD, BHD or OMR store has 3. */
  private readonly currency: CurrencyFormatPipe;

  constructor(
    private readonly realtime: StoreRealtimeService,
    storeContext: StoreContextService,
    toast: ToastService,
    private readonly transloco: TranslocoService,
    private readonly router: Router,
    private readonly zone: NgZone,
    // PosStateService (the lock, the signed-in user) is read from here when
    // first needed, as PrintFailedAlertsService does: AppComponent starts
    // this on every surface, and constructing it runs the POS boot.
    injector: Injector,
  ) {
    this.alerts = new LockAwareAlerts(toast, zone, injector, TC_PAYMENT_HELD_MAX, '[tableconnect.payment.failed]');
    this.currency = new CurrencyFormatPipe(storeContext);
  }

  start(): void {
    if (this.started) return;
    this.started = true;
    this.realtime.event$.subscribe(e => this.zone.run(() => {
      try {
        this.handle(e);
      } catch (err) {
        // A toast that cannot be built must not end the subscription and
        // with it every later alert.
        // eslint-disable-next-line no-console
        console.error('[tableconnect.payment.failed] alert threw — subscription kept', err);
      }
    }));
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(e => this.ensureSocket(e.urlAfterRedirects || e.url));
    this.ensureSocket(this.router.url);
  }

  /** Public for tests. */
  handle(e: StoreEvent | null | undefined): void {
    if (e?.type !== TABLECONNECT_PAYMENT_FAILED) return;
    // Cashier screens only: the KDS, kiosk and the displays share this
    // socket, and the PIN pad has nobody signed in to act on it.
    if (!isPosOperatorScreen(this.router.url)) return;
    const kind = tcPaymentAlertKind(e.status);
    if (!kind) return;
    const message = tcPaymentToastText(
      kind,
      parseTcPaymentSummary(e.summary),
      amount => this.currency.transform(amount, false),
      (key, params) => this.transloco.translate(key, params),
    );
    const review = kind === 'requiresReview';
    this.alerts.deliver(
      `tcPayment|${e.checkId ?? ''}|${message}`,
      message,
      review ? TC_PAYMENT_REVIEW_TOAST_MS : TC_PAYMENT_FAILED_TOAST_MS,
      review ? TC_PAYMENT_REVIEW_HELD_MAX_AGE_MS : TC_PAYMENT_FAILED_HELD_MAX_AGE_MS,
    );
  }

  /**
   * Only pos-shell (`/pos/terminal`) connects the store channel, once its
   * session is known. A terminal that comes up on another operator screen,
   * such as a reload on the order or settle screen, would hear none of these
   * alerts until somebody went back to the floor. So this connects it, as the
   * root caller-ID card does; `ensureConnected` is a no-op once the realtime
   * service is set up for the store. Not on the floor itself, which connects
   * when its session resolves, and not off the operator screens.
   */
  private ensureSocket(url: string | null | undefined): void {
    const path = (url ?? '').split('?')[0].split('#')[0];
    if (!isPosOperatorScreen(path) || path === POS_FLOOR_PATH) return;
    const auth = this.alerts.lockState()?.posAuth;
    if (auth?.tenantId && auth.storeId) this.realtime.ensureConnected(auth.tenantId, auth.storeId);
  }
}

/** The `summary` fields the toast prints, each null when absent or unusable. */
export interface TcPaymentSummary {
  tableName: string | null;
  amount: number | null;
  currency: string | null;
}

/**
 * The event's `summary`, or null when it is missing (an older cloud sends
 * none) or is not a JSON object. Never throws: a malformed summary still gets
 * its toast, without the table or the amount. A check without a table has a
 * null `tableName`.
 */
export function parseTcPaymentSummary(summary: unknown): TcPaymentSummary | null {
  let raw: unknown = summary;
  if (typeof summary === 'string') {
    try {
      raw = JSON.parse(summary);
    } catch {
      return null;
    }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const amount = o['amount'];
  return {
    tableName: text(o['tableName']),
    amount: typeof amount === 'number' && Number.isFinite(amount) ? amount : null,
    currency: text(o['currency']),
  };
}

/**
 * The event's `status`, whatever its case or separators. Null for anything
 * but the two the cloud sends, and that is not toasted: the "Failed" copy
 * says no money was taken, which a later status might not mean.
 */
export function tcPaymentAlertKind(status: unknown): TcPaymentAlertKind | null {
  const raw = typeof status === 'string' ? status : '';
  switch (raw.toLowerCase().replace(/[^a-z]/g, '')) {
    case 'failed': return 'failed';
    case 'requiresreview': return 'requiresReview';
    default: return null;
  }
}

/**
 * The toast, under the keys the shell used (`pos.shell.tcPayment.*`). It names
 * the table, or "no table". `{{amount}}` is the currency code the cloud sent
 * plus the amount from `formatAmount` (the store's decimals), or empty when
 * the summary has no amount; only the RequiresReview copy prints it.
 */
export function tcPaymentToastText(
  kind: TcPaymentAlertKind,
  summary: TcPaymentSummary | null,
  formatAmount: (amount: number) => string,
  translate: (key: string, params?: Record<string, unknown>) => string,
): string {
  const table = summary?.tableName ?? translate('pos.shell.tcPayment.unknownTable');
  const amount = summary?.amount != null && summary.currency
    ? `${summary.currency} ${formatAmount(summary.amount)}`
    : '';
  return translate(
    kind === 'requiresReview' ? 'pos.shell.tcPayment.needsAttention' : 'pos.shell.tcPayment.failed',
    { table, amount },
  );
}

/** A trimmed non-empty string; anything else is absent. */
function text(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const t = v.trim();
  return t.length > 0 ? t : null;
}
